import { useState, useEffect, useCallback, useMemo } from "react";
import FirebaseBiodataRepository from '../api/FirebaseBiodataRepository';
import GetBiodataByIdUseCase from '../use-cases/GetBiodataByIdUseCase';
import UpdateBiodataUseCase from '../use-cases/UpdateBiodataUseCase';

const useSingleBiodataViewModel = (biodataId) => {
  const [biodata, setBiodata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Instantiate repositories and use cases once using useMemo
  const biodataRepository = useMemo(() => new FirebaseBiodataRepository(), []);
  const getBiodataByIdUseCase = useMemo(() => new GetBiodataByIdUseCase(biodataRepository), [biodataRepository]);
  const updateBiodataUseCase = useMemo(() => new UpdateBiodataUseCase(biodataRepository), [biodataRepository]);

  const fetchBiodataDetails = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const fetchedBiodata = await getBiodataByIdUseCase.execute(biodataId);
      if (fetchedBiodata) {
        setBiodata(fetchedBiodata);
      } else {
        setError("Biodata profile not found.");
      }
    } catch (err) {
      console.error("Error fetching biodata details:", err);
      setError("Failed to load biodata for editing.");
    } finally {
      setLoading(false);
    }
  }, [biodataId, getBiodataByIdUseCase]);

  const updateBiodata = useCallback(async (updates) => {
    setError(null);
    try {
      await updateBiodataUseCase.execute(biodataId, updates);
      // Optionally re-fetch or update local state after successful update
      setBiodata((prev) => ({ ...prev, ...updates }));
      return true; // Indicate success
    } catch (err) {
      console.error("Error updating biodata:", err);
      
      // Set specific error message
      if (err.code === 'storage/quota-exceeded') {
        setError("Storage quota exceeded. Cannot upload new images.");
      } else {
        setError(err.message || "Failed to update biodata profile.");
      }
      
      // Re-throw the error so the component can handle it
      throw err;
    }
  }, [biodataId, updateBiodataUseCase]);

  useEffect(() => {
    if (biodataId) {
      fetchBiodataDetails();
    }
  }, [biodataId, fetchBiodataDetails]);

  return {
    biodata,
    loading,
    error,
    fetchBiodataDetails,
    updateBiodata,
  };
};

export default useSingleBiodataViewModel;
